import { toCalendarDate, toTime } from "@internationalized/date";
import { AddOutline30Icon } from "@vygruppen/spor-icon-react";
import { Employee } from "features/CenterContent/RoleContent/VehicleChecklist/hooks/useEmployeeSearch";
import { ChecklistFormData } from "features/CenterContent/RoleContent/VehicleChecklist/schema/formSchema";
import { FillOut } from "features/CenterContent/RoleContent/VehicleChecklist/schema/responseSchema";
import {
  CheckListType,
  CompanyId,
  getChecklistText,
  getCompanyName,
} from "features/CenterContent/RoleContent/VehicleChecklist/utils";
import { VehicleSetVariant } from "features/CenterContent/RoleContent/VehicleChecklist/VehicleSetVariant";
import { FC, useEffect, useState } from "react";
import { UseFormReturn, useFormContext } from "react-hook-form";
import { Select } from "shared/components/forms/Select";
import {
  Combobox,
  DropsDatePicker,
  Field,
  FieldErrorText,
  Flex,
  HStack,
  Item,
  ItemLabel,
  Text,
  Textarea,
  TimePicker,
} from "shared/components/Spor";

interface ChecklistFormContentProps {
  currentFillOut?: FillOut | null;
  vehicleSetVariants: VehicleSetVariant[];
  onVehicleSelect: (value: string, setType: string) => void;
  formMethods: UseFormReturn<ChecklistFormData>;
  employees: Employee[];
  input: string;
  setInput: (input: string) => void;
  potentialSetIdFromSearchString?: string;
}

export const ChecklistFormContent: FC<ChecklistFormContentProps> = ({
  currentFillOut,
  vehicleSetVariants,
  onVehicleSelect,
  formMethods,
  employees,
  input,
  setInput,
  potentialSetIdFromSearchString,
}) => {
  const {
    register,
    setValue,
    formState: { errors },
  } = useFormContext<ChecklistFormData>();
  const { watch } = formMethods;
  const dateTime = watch("dateTime");

  const [rollingStockId, setRollingStockId] = useState<string>(
    currentFillOut?.rollingStock || "",
  );

  useEffect(() => {
    if (currentFillOut?.executor) {
      setInput(`${currentFillOut.executorName} (${currentFillOut.executor})`);
    }
  }, [currentFillOut?.executor]);

  useEffect(() => {
    const setType = vehicleSetVariants.find(
      (variant) => variant.setId === rollingStockId,
    )?.setType;

    if (rollingStockId && setType) {
      setValue("rollingStockId", rollingStockId.toString());
      setValue("vehicleType", setType);
      onVehicleSelect(rollingStockId.toString(), setType);
    }
  }, [rollingStockId]);

  useEffect(() => {
    // Attempt to select a set based on the searched query string
    if (potentialSetIdFromSearchString) {
      const potentialSet = vehicleSetVariants?.find(
        (variant) => variant.setId === potentialSetIdFromSearchString,
      );

      if (potentialSet) {
        setRollingStockId(potentialSet.setId);
      }
    }
  }, []);

  return (
    <Field w="100%">
      {currentFillOut && (
        <Flex gap="5px" pt="20px">
          <Text fontWeight="bold" mb="24px">
            Kvittert av:
          </Text>
          <Text>{currentFillOut.executor}</Text>
        </Flex>
      )}
      <HStack mb="24px">
        <DropsDatePicker
          width="100%"
          minHeight="0"
          variant="core"
          label="Dato"
          value={toCalendarDate(dateTime)}
          onValueChange={(date) => {
            if (date !== null) {
              setValue("dateTime", dateTime.set({ ...date }));
            }
          }}
          withPortal={false}
        />
        <TimePicker
          label="Fra kl"
          minuteInterval={5}
          value={toTime(dateTime)}
          onValueChange={(time) => {
            if (time !== null) {
              setValue("dateTime", dateTime.set({ ...time }));
            }
          }}
        />
      </HStack>
      <Field invalid={!!errors.fillOutType} pb="12px">
        <Select
          label="Kvitteringstype"
          placeholder="Velg type"
          {...register("fillOutType")}
        >
          {Object.values(CheckListType).map((item) => (
            <option key={item} value={item}>
              {getChecklistText(item)}
            </option>
          ))}
        </Select>
        <FieldErrorText>{errors.fillOutType?.message}</FieldErrorText>
      </Field>
      <Field invalid={!!errors.rollingStockId} pb="12px">
        <Combobox
          leftIcon={<AddOutline30Icon />}
          label="Legg til kjøretøy"
          inputValue={rollingStockId}
          onInputChange={setRollingStockId}
          allowsCustomValue
        >
          {vehicleSetVariants &&
          Array.isArray(vehicleSetVariants) &&
          vehicleSetVariants.length > 0 ? (
            vehicleSetVariants.map((variant) => (
              <Item key={variant.setId} textValue={variant.setId}>
                <ItemLabel>{variant.setId}</ItemLabel>
              </Item>
            ))
          ) : (
            <Item>
              <ItemLabel>Ingen kjøretøy tilgjengelig</ItemLabel>
            </Item>
          )}
        </Combobox>
        <FieldErrorText>{errors.rollingStockId?.message}</FieldErrorText>
      </Field>
      <Field invalid={!!errors.trainOwner} pb="12px">
        <Select
          label="Selskap"
          placeholder="Velg selskap"
          {...register("trainOwner")}
        >
          {Object.values(CompanyId).map((item) => (
            <option key={item} value={item as CompanyId}>
              {getCompanyName(item as CompanyId)}
            </option>
          ))}
        </Select>
        <FieldErrorText>{errors.trainOwner?.message}</FieldErrorText>
      </Field>
      <Field
        invalid={!!errors.executor}
        /**
         * We need to put a maxHeight on employee items or else big searches at the bottom of
         * the modal will overflow the modal and cause a scroll -> defocus -> re-render loop.
         * */
        css={{ ul: { maxHeight: "175px" } }}
      >
        <Combobox
          leftIcon={<AddOutline30Icon />}
          label="Kvittert av (ansattnummer)"
          inputValue={input}
          onInputChange={setInput}
          onSelectionChange={(key) => {
            const selectedEmployee = employees.find(
              (employee) => employee.employeeId === key,
            );
            if (selectedEmployee) {
              setValue("executor", selectedEmployee.employeeId);
            }
          }}
          items={employees} // Necessary for combobox to re-render when employees are updated
          allowsCustomValue
        >
          {employees.length > 0 ? (
            employees.map((employee) => (
              <Item
                key={employee.employeeId}
                textValue={`${employee.displayName} (${employee.employeeId})`}
              >
                {`${employee.displayName} (${employee.employeeId})`}
              </Item>
            ))
          ) : (
            <Item key="no-employees" textValue="Ingen ansatte funnet">
              <ItemLabel>Ingen ansatte funnet</ItemLabel>
            </Item>
          )}
        </Combobox>
        <FieldErrorText>{errors.executor?.message}</FieldErrorText>
      </Field>
      <Field invalid={!!errors.comment} pt="18px">
        <Text>Kommentar</Text>
        <Textarea label="Oppgi årsak" {...register("comment")} height="130px" />
        <FieldErrorText>{errors.comment?.message}</FieldErrorText>
      </Field>
    </Field>
  );
};
